import { useState } from 'react';
import { motion } from 'framer-motion';

const FORM_URL = 'https://readdy.ai/api/form/d709qlb2m2156g4s6pj0';

export default function ReviewUpload() {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [reviewerEmail, setReviewerEmail] = useState('');
  const [serviceUsed, setServiceUsed] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (rating === 0) return;

    setIsSubmitting(true);
    setIsError(false);

    const payload = new URLSearchParams();
    payload.append('name', reviewerName);
    payload.append('email', reviewerEmail);
    payload.append('rating', String(rating));
    payload.append('service', serviceUsed);
    payload.append('review', reviewText);

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      if (res.ok) {
        setIsSuccess(true);
        setRating(0);
        setReviewText('');
        setReviewerName('');
        setReviewerEmail('');
        setServiceUsed('');
        setTimeout(() => setIsSuccess(false), 5000);
      } else {
        setIsError(true);
      }
    } catch {
      setIsError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-dark-800 to-dark-900 relative overflow-hidden">
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-orbitron text-5xl font-bold text-white mb-4">
            SHARE YOUR <span className="text-electric-blue">EXPERIENCE</span>
          </h2>
          <p className="font-inter text-white/60 text-lg">
            Your feedback helps us improve our services
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-white/10 backdrop-blur-xl border border-electric-blue/30 rounded-3xl p-8"
        >
          {isSuccess && (
            <div className="mb-6 flex items-center gap-3 bg-green-500/20 border border-green-500/40 rounded-xl px-5 py-4">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className="ri-checkbox-circle-fill text-green-400 text-xl"></i>
              </div>
              <div>
                <p className="font-inter text-green-300 text-sm font-semibold">Thank you for your review!</p>
                <p className="font-inter text-green-300/70 text-xs mt-0.5">Your feedback has been submitted successfully.</p>
              </div>
            </div>
          )}

          {isError && (
            <div className="mb-6 flex items-center gap-3 bg-red-500/20 border border-red-500/40 rounded-xl px-5 py-4">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-fill text-red-400 text-xl"></i>
              </div>
              <p className="font-inter text-red-300 text-sm">
                Something went wrong. Please try again or call us at (415) 888-5336.
              </p>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="space-y-6"
          >
            {/* Star Rating */}
            <div>
              <label className="block font-inter text-white/80 text-sm mb-4 text-center">
                Rate Your Experience <span className="text-red-400">*</span>
              </label>
              <div className="flex justify-center space-x-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="transition-transform duration-200 hover:scale-110 cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center">
                      <i
                        className={`${
                          star <= (hoveredRating || rating)
                            ? 'ri-star-fill text-yellow-400'
                            : 'ri-star-line text-white/30'
                        } text-5xl`}
                      ></i>
                    </div>
                  </button>
                ))}
              </div>
              {rating === 0 && (
                <p className="text-center text-white/40 text-xs font-inter mt-2">Please select a star rating</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label className="block font-inter text-white/80 text-sm mb-2">
                  Your Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={reviewerName}
                  onChange={(e) => setReviewerName(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white font-inter text-sm focus:outline-none focus:border-electric-blue transition-colors duration-300"
                  placeholder="Enter your name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block font-inter text-white/80 text-sm mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={reviewerEmail}
                  onChange={(e) => setReviewerEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white font-inter text-sm focus:outline-none focus:border-electric-blue transition-colors duration-300"
                  placeholder="your@email.com (optional)"
                />
              </div>
            </div>

            {/* Service Used */}
            <div>
              <label className="block font-inter text-white/80 text-sm mb-2">
                Service Used
              </label>
              <select
                name="service"
                value={serviceUsed}
                onChange={(e) => setServiceUsed(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white font-inter text-sm focus:outline-none focus:border-electric-blue transition-colors duration-300 cursor-pointer"
              >
                <option value="">Select the service you used</option>
                <option value="Residential Relocation">Residential Relocation</option>
                <option value="Commercial Interstate">Commercial Interstate</option>
                <option value="Intrastate Relocations">Intrastate Relocations</option>
                <option value="General Freight">General Freight</option>
                <option value="Building Materials">Building Materials</option>
                <option value="Vehicle Transport">Vehicle Transport</option>
              </select>
            </div>

            {/* Review Text */}
            <div>
              <label className="block font-inter text-white/80 text-sm mb-2">
                Your Review <span className="text-red-400">*</span>
                <span className="ml-2 text-white/40 text-xs">({reviewText.length}/500)</span>
              </label>
              <textarea
                name="review"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                required
                rows={6}
                maxLength={500}
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white font-inter text-sm focus:outline-none focus:border-electric-blue transition-colors duration-300 resize-none"
                placeholder="Share your experience with LONE TRS..."
              />
              {reviewText.length >= 500 && (
                <p className="text-yellow-400 text-xs mt-1 font-inter">Maximum 500 characters reached.</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting || isSuccess || rating === 0}
              className="w-full py-4 bg-gradient-to-r from-electric-blue to-electric-cyan text-dark-900 font-orbitron font-bold text-base rounded-full hover:opacity-90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                  </div>
                  <span>SUBMITTING...</span>
                </span>
              ) : isSuccess ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-checkbox-circle-fill text-xl"></i>
                  </div>
                  <span>REVIEW SUBMITTED!</span>
                </span>
              ) : (
                'SUBMIT REVIEW'
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
